'use client';

import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import Link from 'next/link';
import { useTranslation } from 'react-i18next';
import { useScrollLock } from '@akademi/ui';
import { useBlockControlStamp, type MediaVideoDetail } from '@akademi/site-builder-runtime';

/**
 * Detail modal opened from {@link MediaCardRow}. Renders the
 * video's full metadata (poster, title, description, director, tags)
 * + a "Watch" CTA pointing at the per-video route.
 */
export interface MediaDetailModalProps {
  open: boolean;
  onClose: () => void;
  video: MediaVideoDetail | null;
  loading?: boolean;
  ctaLabel?: string;
  videoHrefTemplate?: string;
}

export function MediaDetailModal({
  open,
  onClose,
  video,
  loading = false,
  ctaLabel: authoredCtaLabel,
  videoHrefTemplate = '/tube/{slug}',
}: MediaDetailModalProps) {
  const { t } = useTranslation('site-builder-blocks');
  const ctaLabel = authoredCtaLabel ?? t('mediaDetail.cta');
  const [mounted, setMounted] = useState(false);
  useEffect(() => setMounted(true), []);
  // The modal portals to document.body — outside the owning block's DOM
  // subtree — so its controls need their own data-<ns>-el pass.
  const stampRef = useBlockControlStamp<HTMLDivElement>({ portal: true });

  useScrollLock(open);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  if (!open || !mounted) return null;

  const node = (
    <div
      ref={stampRef}
      role="dialog"
      aria-modal="true"
      onClick={onClose}
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(15,23,42,0.7)',
        backdropFilter: 'blur(4px)',
        zIndex: 9999,
        display: 'grid',
        placeItems: 'center',
        padding: 'clamp(8px, 2vw, 24px)',
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          width: '100%',
          maxWidth: 720,
          background: 'var(--color-surface-card, #fff)',
          borderRadius: 12,
          overflow: 'hidden',
          boxShadow: 'var(--shadow-xl, 0 24px 48px rgba(0,0,0,0.18))',
        }}
      >
        {loading || !video ? (
          <div
            style={{
              padding: 64,
              textAlign: 'center',
              color: 'var(--color-text-secondary, #71717a)',
            }}
          >
            {t('mediaDetail.loading')}
          </div>
        ) : (
          <>
            <div
              aria-hidden
              style={{
                aspectRatio: '16 / 9',
                background: video.posterUrl
                  ? `url(${video.posterUrl}) center/cover no-repeat`
                  : 'linear-gradient(135deg, var(--color-brand-500, #4f46e5), var(--color-brand-700, #312e81))',
              }}
            />
            <div style={{ padding: 24, display: 'flex', flexDirection: 'column', gap: 12 }}>
              <h2 style={{ margin: 0, fontSize: 'var(--font-size-xl, 22px)', fontWeight: 600 }}>
                {video.title}
              </h2>
              <div
                style={{
                  fontSize: 'var(--font-size-xs, 12px)',
                  color: 'var(--color-text-tertiary, #94867d)',
                  fontFamily: 'var(--font-family-mono, monospace)',
                }}
              >
                {video.releaseYear && <>{video.releaseYear}</>}
                {video.releaseYear && video.durationLabel && ' · '}
                {video.durationLabel}
                {video.director && ` · ${t('mediaDetail.director', { name: video.director })}`}
              </div>
              {video.description && (
                <p style={{ margin: 0, fontSize: 'var(--font-size-sm, 14px)', lineHeight: 1.6 }}>
                  {video.description}
                </p>
              )}
              {video.tags && video.tags.length > 0 && (
                <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                  {video.tags.map((t) => (
                    <span
                      key={t}
                      style={{
                        padding: 'var(--space-1, 4px) var(--space-2-5, 10px)',
                        background: 'var(--color-neutral-100, #f4f4f5)',
                        borderRadius: 999,
                        fontSize: '11px',
                        fontWeight: 500,
                      }}
                    >
                      {t}
                    </span>
                  ))}
                </div>
              )}
              <Link
                href={videoHrefTemplate.replace('{slug}', encodeURIComponent(video.slug))}
                onClick={onClose}
                style={{
                  alignSelf: 'flex-start',
                  marginTop: 8,
                  padding: 'var(--space-2-5, 10px) var(--space-5, 20px)',
                  background: 'var(--color-text-primary, #18181b)',
                  color: 'var(--color-text-on-brand, #fff)',
                  borderRadius: 8,
                  textDecoration: 'none',
                  fontWeight: 600,
                  fontSize: 'var(--font-size-sm, 14px)',
                }}
              >
                ▶ {ctaLabel}
              </Link>
            </div>
          </>
        )}
      </div>
    </div>
  );

  return createPortal(node, document.body);
}
